import {environment} from '../../environments/environment';

/*
 * The public game page's data, from the backend's GET boardgames/{slug}/public-page (LUD-345).
 * These types mirror the backend responses, trimmed to the fields the landing renders. kotlinx
 * omits empty collections and default values, so every list and optional flag is optional here.
 */

export type Availability = 'IN_STOCK' | 'OUT_OF_STOCK' | 'PREORDER' | 'UNKNOWN';

export interface PublicUser {
    id: string;
    username: string;
    name: string;
    avatarUrl?: string | null;
    /** Set for group accounts (clubs, publishers, shops), which the app draws as hexagons. */
    groupType?: string | null;
}

export interface PublicGameBasic {
    id: string;
    slug: string;
    name: string;
    imageId?: string | null;
    yearPublished?: number | null;
}

export interface PublicTag {
    id: string;
    slug: string;
    name: string;
}

export interface PublicGame extends PublicGameBasic {
    description?: string | null;
    type: string;
    minPlayerCount: number;
    maxPlayerCount: number;
    bestPlayerCount: number;
    /** The counts it is best at: Ludoya's players' answer, else BGG's count (LUD-591). Absent when none. */
    bestPlayerCounts?: number[];
    /** True when bestPlayerCounts is Ludoya's own answer rather than BGG's fallback. */
    bestPlayerCountsFromLudoya?: boolean;
    minPlayTimeMinutes: number;
    maxPlayTimeMinutes: number;
    minAge: number;
    complexity: number;
    bggUrl?: string | null;
    hasAi: boolean;
    categories?: PublicTag[];
    mechanics?: PublicTag[];
    ludoyaRating?: number | null;
    lowestPrice?: number | null;
    lowestPriceCurrency?: string | null;
    lowestPriceAvailability?: Availability | null;
}

export interface PublicShopItem {
    id: string;
    shop: {id: string; name: string; url: string; logoUrl: string; country: string};
    name: string;
    price: string;
    currencySymbol: string;
    availability: Availability;
    url: string;
}

export interface PublicListing {
    id: string;
    trader: PublicUser;
    price?: number | null;
    currency: string;
    condition: string;
    location: {city?: string | null; countryCode?: string | null};
    images?: {url: string; previewUrl: string}[] | null;
}

export interface PublicPricePoint {
    date: string;
    priceEur: number;
    availability: Availability;
}

export interface PublicReview {
    id: string;
    creatorUser?: PublicUser | null;
    rating: number;
    /** The review in one line. */
    headline?: string | null;
    /** The reviewer's logged plays of the game; absent when they logged none. */
    plays?: {plays: number} | null;
    /** What the reviewer discloses: a free copy, paid, works with the publisher, a preview copy. */
    disclosures?: ('FREE_COPY' | 'PAID' | 'AFFILIATED' | 'PREVIEW')[] | null;
    /** The reviewer's own video or full review elsewhere. */
    video?: {url: string; authorName?: string | null} | null;
    link?: {url: string} | null;
    text?: string | null;
    /** The text gives away story or surprises: a public page shows the verdict line alone. */
    hasSpoilers?: boolean | null;
    textTranslation?: {detectedLang: string; translation?: {lang: string; value: string} | null} | null;
    createdAt: string;
}

/** BGG's five language-dependence levels, in order, as Ludoya reviews answer them. */
export type PublicReviewReading = 'NONE' | 'SOME' | 'MODERATE' | 'EXTENSIVE' | 'UNPLAYABLE';

export interface PublicReviews {
    totalReviews: number;
    averageRating?: number | null;
    /** What reviewers said per player count, lowest count first. */
    playerCountConsensus?: {players: number; best: number; good: number; skip: number}[] | null;
    /** The youngest age reviewers give it (LUD-594): their median once three answered. */
    youngestAge?: {age?: number | null; reviewers: number} | null;
    /** How much reading reviewers say it asks of the table: their median level once three answered. */
    reading?: {reading?: PublicReviewReading | null; reviewers: number} | null;
    otherReviews?: PublicReview[];
}

export interface PublicCommunityStats {
    ownershipCounts: {owned: number; previouslyOwned: number; wishlisted: number; preordered: number};
    playStats: {
        stats: {totalPlayCount: number; uniquePlayers: number; playsWithDuration: number; averagePlayTime: string};
    };
}

export interface PublicEvent {
    id: string;
    title: string;
    imageUrl?: string | null;
    /** The event's own picture at full size (its preview is only 100px); absent for game images. */
    imageFullUrl?: string | null;
    startsAt?: string | null;
    endsAt?: string | null;
    /** The event's own zone: the server renders in UTC, and a game night's time is local to it. */
    timeZone?: string | null;
    /** One line of plain description, for structured data. */
    descriptionExcerpt?: string | null;
    /** Cheapest public seat in minor units (0 = free); absent when the price is not ours to quote. */
    priceFromMinor?: number | null;
    priceCurrency?: string | null;
    /** When sign-ups open, if the organizer scheduled it; the offer's validFrom. */
    opensAt?: string | null;
    canceled: boolean;
    goingCount: number;
    location?: {name?: string | null; city?: string | null} | null;
}

export interface PublicPhoto {
    url: string;
    previewUrl: string;
    thumbnailUrl?: string | null;
    source: {type: string; id: string; creatorUser: PublicUser};
}

export interface PublicVersion {
    id: string;
    name: string;
    label?: string | null;
    languages?: string[];
    yearPublished?: number | null;
    imageId?: string | null;
    publisher?: PublicUser | null;
}

export interface PublicKit {
    id: string;
    name: string;
    usageCount: number;
}

export interface PublicGameWidgets {
    versions?: {list?: PublicVersion[]} | null;
    expansions?: {expansions?: PublicGameBasic[]; bases?: PublicGameBasic[]; integrations?: PublicGameBasic[]} | null;
    similar?: PublicGameBasic[] | null;
    /** `versionPublishers` marks the edition/localization publishers block, which this page leaves out. */
    creators?: {list?: {type: string; versionPublishers?: boolean; creators?: PublicUser[]}[]} | null;
    photos?: {elements?: PublicPhoto[]; totalNumber?: number} | null;
    events?: {futureEvents?: {elements?: PublicEvent[]}; futureCount?: number} | null;
    gameKits?: {elements?: PublicKit[]; totalNumber?: number} | null;
}

export interface RulesFaqEntry {
    question: string;
    answer: string;
}

export interface PublicGamePage {
    game: PublicGame;
    indexable: boolean;
    updatedAt: string;
    country: string;
    currency: string;
    storeOffers?: PublicShopItem[];
    listings?: PublicListing[];
    priceHistory?: PublicPricePoint[];
    communityStats?: PublicCommunityStats | null;
    reviews?: PublicReviews | null;
    alsoOwned?: PublicGameBasic[];
    rulesFaq?: RulesFaqEntry[];
    /** The FAQ is still being generated or translated: [rulesFaq] is empty or English for now. */
    rulesFaqPending?: boolean;
    /** The game has a printable score sheet page, from its official kit (LUD-411). */
    scoreSheet?: boolean;
    widgets: PublicGameWidgets;
}

export type PublicGameResult =
    | {kind: 'page'; page: PublicGamePage}
    | {kind: 'redirect'; slug: string}
    | {kind: 'not-found'};

/** On the server the API is reached in-cluster; the API_URL env var wins over the build-time value. */
function apiBase(): string {
    if (typeof window === 'undefined') {
        return (typeof process !== 'undefined' && process.env['API_URL']) || environment.serverApiUrl;
    }
    return environment.apiUrl;
}

/**
 * [userAgent] is the visitor's, not this server's: the API generates and translates AI text only for a
 * person, and every public page is otherwise indistinguishable from the landing's own in-cluster call.
 */
export async function fetchPublicGame(slug: string, locale: string, userAgent?: string | null): Promise<PublicGameResult> {
    const res = await fetch(`${apiBase()}/boardgames/${encodeURIComponent(slug)}/public-page`, {
        headers: {
            'Accept-Language': locale,
            ...(userAgent ? {'X-Visitor-User-Agent': userAgent} : {}),
        },
        signal: AbortSignal.timeout(15_000),
    });
    if (res.status === 404) {
        return {kind: 'not-found'};
    }
    if (!res.ok) {
        throw new Error(`GET /boardgames/${slug}/public-page: HTTP ${res.status}`);
    }
    const body = await res.json() as PublicGamePage | {redirectSlug: string};
    return 'redirectSlug' in body ? {kind: 'redirect', slug: body.redirectSlug} : {kind: 'page', page: body};
}

/** Public images: Cloudflare R2 behind a CDN (LUD-529); index.html falls back to the OVH bucket. */
const IMAGE_BASE_URL = 'https://images.ludoya.com/';

/** Same bucket layout the app's gameImage util reads. */
export function gameImageUrl(imageId: string | null | undefined, size: 'full' | 'preview' | 'thumbnail' = 'preview'): string {
    if (!imageId) {
        return `${IMAGE_BASE_URL}assets/no-game.png`;
    }
    const suffix = size === 'full' ? '' : `_${size}`;
    return `${IMAGE_BASE_URL}${imageId}${suffix}.jpg`;
}

/** App section routes, as the app's right drawer maps them (`/g/{slug}/{section}`). */
export type AppGameSection = 'reviews' | 'prices' | 'events' | 'photos' | 'plays' | 'helpers' | 'expansions' | 'editions' | 'stats' | 'community';

export function appGameUrl(slug: string, section?: AppGameSection): string {
    return `${environment.appUrl}/g/${encodeURIComponent(slug)}${section ? '/' + section : ''}`;
}

export function appAiChatUrl(slug: string): string {
    return `${environment.appUrl}/ai-chat/${encodeURIComponent(slug)}`;
}

export function appEventUrl(id: string): string {
    return `${environment.appUrl}/events/${encodeURIComponent(id)}`;
}

export function appListingUrl(id: string): string {
    return `${environment.appUrl}/marketplace/listing/${encodeURIComponent(id)}`;
}

export function appUserUrl(username: string): string {
    return `${environment.appUrl}/${encodeURIComponent(username)}`;
}

/**
 * A review as written in the app, flattened for a page that shows it as plain text. The app's
 * composer stores star ratings as `:star:`/`:nostar:` tokens and the body as markdown
 * (PostBodyComponent renders both); here they would otherwise show up verbatim.
 */
export function reviewPlainText(text: string): string {
    return text
        .replace(/:star:/g, '★')
        .replace(/:nostar:/g, '☆')
        .replace(/!\[([^\]]*)]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
        .replace(/^\s*#{1,6}\s+/gm, '')
        .replace(/^\s*>\s?/gm, '')
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        // The word-character guards keep snake_case names intact while still catching _largo_.
        .replace(/(?<![A-Za-z0-9_])([*_])(\S(?:.*?\S)?)\1(?![A-Za-z0-9_])/g, '$2')
        .replace(/`([^`]*)`/g, '$1')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
